import Box from '@sendsprint/ui-react/dist/components/Box';
import Button from '@sendsprint/ui-react/dist/components/Button';
import Skeleton from '@sendsprint/ui-react/dist/components/Skeleton';
import lazyWithPreload from '@src/utils/lazyWithPreload';
import React from 'react';
import ErrorFallback from '../errorFallback';
import SuspenseWrapper from '../suspenseWrapper';
import { resolveTxRef } from './utils/resolveTxref';
import { PayBtnClickHandlerOptions, PaymentMethodI, SortedDataI } from '.';
import PaymentCodeDropdown from './components/paymentCodeDropdown';

export const FlutterwaveDropdown = lazyWithPreload(
  () => import(/*webpackChunkName: 'FlutterwaveDropdown'*/ './components/flutterwaveDropdown')
);
export const ModulrDropdown = lazyWithPreload(
  () => import(/*webpackChunkName: 'ModulrDropdown'*/ './components/modulrDropdown')
);
export const VolumeDropdown = lazyWithPreload(
  () => import(/*webpackChunkName: 'VolumeDropdown'*/ './components/volumeDropdown')
);
export const WirefxDropdown = lazyWithPreload(
  () => import(/*webpackChunkName: 'WirefxDropdown'*/ './components/wirefxDropdown')
);
export const StripeDropdown = lazyWithPreload(
  () => import(/*webpackChunkName: 'StripeDropdown'*/ './components/stripeDropdown')
);
export const CitconDropdown = lazyWithPreload(
  () => import(/*webpackChunkName: 'CitconDropdown'*/ './components/citconDropdown')
);
export const CheckoutDropdown = lazyWithPreload(
  () => import(/*webpackChunkName: 'CheckoutDropdown'*/ './components/checkoutDropdown')
);

interface Props {
  sortedData: SortedDataI[];
  currency: string;
  handleOtherPaymentMethods: () => void;
  // eslint-disable-next-line no-unused-vars
  handleToggle: (id: number) => void;
  isNethoneCheck: boolean;
  isSubmitting: boolean;
  selectedPaymentMethod: PaymentMethodI | null;
  value: number;
  markPageAsClean: () => void;
  transferType: string;
  onSuccess?: () => void;
  showVolumeModal: boolean;
  handleModalClose: () => void;
  handleModalOpen: () => void;
  txRef?: string;
  modulrURL?: string;
  stripeURL?: string;
  checkoutURL?: string;
  citconURL?: string;
  onCancelBtnClickHandler: () => void;
  handlePayment: (
    variant: PaymentMethodI,
    options?: PayBtnClickHandlerOptions
  ) => void | Promise<boolean>;
  shouldShowOnlyPaymentCodeBtn?: boolean;
  paymentCodeValue?: number;
  cancelTransactionLoading?: boolean;
  handleCancelTransaction?: () => Promise<void>;
}

const PaymentDropdownListInner = ({
  sortedData,
  currency,
  handleOtherPaymentMethods,
  handleToggle,
  isNethoneCheck,
  isSubmitting,
  selectedPaymentMethod,
  value,
  markPageAsClean,
  transferType,
  onSuccess,
  showVolumeModal,
  handleModalClose,
  handleModalOpen,
  txRef,
  modulrURL,
  stripeURL,
  citconURL,
  onCancelBtnClickHandler,
  paymentCodeValue,
  shouldShowOnlyPaymentCodeBtn,
  handlePayment,
  cancelTransactionLoading,
  handleCancelTransaction,
  checkoutURL
}: Props) => {
  return (
    <Box className="ss-bg-white ss-p-3 ss-py-4 md:ss-p-6 ss-space-y-2 ss-rounded-lg">
      {!shouldShowOnlyPaymentCodeBtn &&
        sortedData &&
        sortedData?.map((item) => {
          if (item.Gateway === 'VolumePay') {
            return (
              <SuspenseWrapper
                key={item.Id}
                errorFallback={(errorObj) => (
                  <ErrorFallback
                    imageSize="small"
                    text="Error loading payment method"
                    className="ss-p-2 ss-border ss-border-neutral-100 ss-rounded"
                    {...errorObj}
                  />
                )}
                suspenseFallback={<Skeleton className="ss-rounded ss-h-32" />}>
                <VolumeDropdown
                  key={item.Id}
                  currency={currency}
                  handleOtherPaymentMethods={handleOtherPaymentMethods}
                  handlePayment={item.handleSubmit}
                  handleToggle={() => handleToggle(item.Id)}
                  isNethoneCheck={isNethoneCheck}
                  isOpen={item.isOpen}
                  isSubmitting={isSubmitting}
                  txRef={resolveTxRef(selectedPaymentMethod, 'volume', txRef)}
                  value={value}
                  markPageAsClean={markPageAsClean}
                  transferType={transferType}
                  onSuccess={onSuccess}
                  showVolumeModal={showVolumeModal}
                  handleModalClose={handleModalClose}
                  handleModalOpen={handleModalOpen}
                  cancelTransactionLoading={cancelTransactionLoading}
                  handleCancelTransaction={handleCancelTransaction}
                />
              </SuspenseWrapper>
            );
          }

          if (item.Gateway === 'Modulr') {
            return (
              <SuspenseWrapper
                key={item.Id}
                errorFallback={(errorObj) => (
                  <ErrorFallback
                    imageSize="small"
                    text="Error loading payment method"
                    className="ss-p-2 ss-border ss-border-neutral-100 ss-rounded"
                    {...errorObj}
                  />
                )}
                suspenseFallback={<Skeleton className="ss-rounded ss-h-32" />}>
                <ModulrDropdown
                  key={item.Id}
                  currency={currency}
                  handlePayment={item.handleSubmit}
                  handleToggle={() => handleToggle(item.Id)}
                  isNethoneCheck={isNethoneCheck}
                  isOpen={item.isOpen}
                  isSubmitting={isSubmitting}
                  txRef={resolveTxRef(selectedPaymentMethod, 'modulr', txRef)}
                  value={value}
                  description={item.Description}
                  modulrURL={modulrURL}
                  cancelTransactionLoading={cancelTransactionLoading}
                  handleCancelTransaction={handleCancelTransaction}
                />
              </SuspenseWrapper>
            );
          }

          if (item.Gateway === 'Stripe') {
            return (
              <SuspenseWrapper
                key={item.Id}
                errorFallback={(errorObj) => (
                  <ErrorFallback
                    imageSize="small"
                    text="Error loading payment method"
                    className="ss-p-2 ss-border ss-border-neutral-100 ss-rounded"
                    {...errorObj}
                  />
                )}
                suspenseFallback={<Skeleton className="ss-rounded ss-h-32" />}>
                <StripeDropdown
                  key={item.Id}
                  currency={currency}
                  handlePayment={item.handleSubmit}
                  handleToggle={() => handleToggle(item.Id)}
                  isNethoneCheck={isNethoneCheck}
                  isOpen={item.isOpen}
                  isSubmitting={isSubmitting}
                  txRef={resolveTxRef(selectedPaymentMethod, 'stripe', txRef)}
                  value={value}
                  description={item.Description}
                  stripeURL={stripeURL}
                  handleCancelTransaction={handleCancelTransaction}
                />
              </SuspenseWrapper>
            );
          }

          if (item.Gateway === 'Citcon') {
            return (
              <SuspenseWrapper
                key={item.Id}
                errorFallback={(errorObj) => (
                  <ErrorFallback
                    imageSize="small"
                    text="Error loading payment method"
                    className="ss-p-2 ss-border ss-border-neutral-100 ss-rounded"
                    {...errorObj}
                  />
                )}
                suspenseFallback={<Skeleton className="ss-rounded ss-h-32" />}>
                <CitconDropdown
                  key={item.Id}
                  currency={currency}
                  handlePayment={item.handleSubmit}
                  handleToggle={() => handleToggle(item.Id)}
                  isNethoneCheck={isNethoneCheck}
                  isOpen={item.isOpen}
                  isSubmitting={isSubmitting}
                  txRef={resolveTxRef(selectedPaymentMethod, 'citcon', txRef)}
                  value={value}
                  description={item.Description}
                  citconURL={citconURL}
                  handleCancelTransaction={handleCancelTransaction}
                />
              </SuspenseWrapper>
            );
          }

          if (item.Gateway === 'Checkout') {
            return (
              <SuspenseWrapper
                key={item.Id}
                errorFallback={(errorObj) => (
                  <ErrorFallback
                    imageSize="small"
                    text="Error loading payment method"
                    className="ss-p-2 ss-border ss-border-neutral-100 ss-rounded"
                    {...errorObj}
                  />
                )}
                suspenseFallback={<Skeleton className="ss-rounded ss-h-32" />}>
                <CheckoutDropdown
                  key={item.Id}
                  currency={currency}
                  handlePayment={item.handleSubmit}
                  handleToggle={() => handleToggle(item.Id)}
                  isNethoneCheck={isNethoneCheck}
                  isOpen={item.isOpen}
                  isSubmitting={isSubmitting}
                  txRef={resolveTxRef(selectedPaymentMethod, 'checkout', txRef)}
                  value={value}
                  description={item.Description}
                  checkoutURL={checkoutURL}
                  handleCancelTransaction={handleCancelTransaction}
                />
              </SuspenseWrapper>
            );
          }

          if (item.Gateway === 'Flutterwave') {
            return (
              <SuspenseWrapper
                key={item.Id}
                errorFallback={(errorObj) => (
                  <ErrorFallback
                    imageSize="small"
                    text="Error loading payment method"
                    className="ss-p-2 ss-border ss-border-neutral-100 ss-rounded"
                    {...errorObj}
                  />
                )}
                suspenseFallback={<Skeleton className="ss-rounded ss-h-32" />}>
                <FlutterwaveDropdown
                  key={item.Id}
                  currency={currency}
                  handlePayment={item.handleSubmit}
                  handleToggle={() => handleToggle(item.Id)}
                  isNethoneCheck={isNethoneCheck}
                  isOpen={item.isOpen}
                  isSubmitting={isSubmitting}
                  txRef={resolveTxRef(selectedPaymentMethod, 'flutterwave', txRef)}
                  value={value}
                  cancelTransactionLoading={cancelTransactionLoading}
                />
              </SuspenseWrapper>
            );
          }

          if (item.Gateway === 'WireFX') {
            return (
              <SuspenseWrapper
                key={item.Id}
                errorFallback={(errorObj) => (
                  <ErrorFallback
                    imageSize="small"
                    text="Error loading payment method"
                    className="ss-p-2 ss-border ss-border-neutral-100 ss-rounded"
                    {...errorObj}
                  />
                )}
                suspenseFallback={<Skeleton className="ss-rounded ss-h-32" />}>
                <WirefxDropdown
                  key={item.Id}
                  currency={currency}
                  handlePayment={item.handleSubmit}
                  handleToggle={() => handleToggle(item.Id)}
                  isNethoneCheck={isNethoneCheck}
                  isOpen={item.isOpen}
                  isSubmitting={isSubmitting}
                  txRef={resolveTxRef(selectedPaymentMethod, 'wirefx', txRef)}
                  description={item.Description}
                  value={value}
                  markPageAsClean={markPageAsClean}
                  cancelTransactionLoading={cancelTransactionLoading}
                />
              </SuspenseWrapper>
            );
          }

          return null;
        })}

      {shouldShowOnlyPaymentCodeBtn && (
        <PaymentCodeDropdown
          currency={currency}
          isNethoneCheck={isNethoneCheck}
          isSubmitting={isSubmitting}
          txRef={txRef}
          value={value}
          handlePayment={handlePayment}
          paymentCodeValue={paymentCodeValue}
        />
      )}

      <Button
        type="button"
        isBlock={true}
        label="Cancel transfer"
        variant="tertiary"
        disabled={isSubmitting}
        onClick={onCancelBtnClickHandler}
      />
    </Box>
  );
};

export default PaymentDropdownListInner;
